import { useCallback } from "react";

export enum StorageType {
  Local = "local",
  Session = "session",
}

export function useStorage(storageType: StorageType = StorageType.Local) {
  const storage = typeof window !== "undefined"
    ? storageType === StorageType.Local
      ? window.localStorage
      : window.sessionStorage
    : null;

  // 📌 Función para obtener un valor del storage (la clave y el tipo `T` se pasan al llamar)
  const get = useCallback(<T>(key: string): T | null => {
    if (!storage || !key) return null;

    try {
      const rawValue = storage.getItem(key);
      return rawValue ? (JSON.parse(rawValue) as T) : null;
    } catch (error) {
      console.info(`No se pudo obtener el valor de "${key}" en ${storageType}Storage:`, error);
      return null;
    }
  }, [storage, storageType]);

  // 📌 Función para guardar un valor en el storage
  const set = useCallback(<T>(key: string, value: T): void => {
    if (!storage || !key) return;

    try {
      storage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error(`Error al escribir en la key "${key}" en ${storageType}Storage:`, error);
    }
  }, [storage, storageType]);

  // 📌 Función para eliminar un valor del storage
  const remove = useCallback((key: string): void => {
    if (!storage || !key) return;

    try {
      storage.removeItem(key);
    } catch (error) {
      console.error(`Error al eliminar la key "${key}" en ${storageType}Storage:`, error);
    }
  }, [storage, storageType]);

  return { get, set, remove };
}
